import { useQuery } from "@tanstack/react-query";

import { getFieldPracticePoints } from "services";

import { ArrayUtils } from "utils";

import { FieldPracticePoints, PracticeSlug } from "models";

export const FIELD_PRACTICE_POINTS_QUERY_KEY = "field-practice-points";

interface UseGetFieldPracticePointsOptions {
  farmId: string;
  /**
   * Optional filter to only return points for specific practice slugs.
   * If not provided, returns points for all practices.
   */
  practiceSlugs?: PracticeSlug[];
  /**
   * Optional refetch interval in milliseconds.
   * Useful for auto-refreshing after practice updates.
   */
  refetchInterval?: number | false;
}

/**
 * Hook to fetch field-practice-specific points (credits and payouts).
 * Returns initial (at verification start) and latest values per field per practice.
 *
 * Use this hook in practice-specific views (crops, fertilizers, etc.) to display
 * credits and payouts that are relevant to each practice type.
 */
export const useGetFieldPracticePoints = ({ farmId, practiceSlugs, refetchInterval = false }: UseGetFieldPracticePointsOptions) => {
  const query = useQuery({
    queryKey: [FIELD_PRACTICE_POINTS_QUERY_KEY, farmId],
    queryFn: () => getFieldPracticePoints({ farmId }),
    enabled: !!farmId,
    refetchInterval,
    staleTime: 30000, // Consider data fresh for 30 seconds (manual refetch handles updates)
  });

  // Filter by practice slugs if provided
  const filteredPoints: FieldPracticePoints[] | undefined = query.data?.fieldPracticePoints
    ? practiceSlugs
      ? query.data.fieldPracticePoints.filter((point) => practiceSlugs.includes(point.practiceSlug as PracticeSlug))
      : query.data.fieldPracticePoints
    : undefined;

  // Create a lookup map for quick access: fieldId -> points for that field (filtered by practice)
  const pointsByFieldId = filteredPoints ? ArrayUtils.groupBy(filteredPoints, (point) => point.fieldId) : undefined;

  // Helper to get aggregated points for a specific field (sum of all matching practices)
  const getFieldTotals = (fieldId: string) => {
    const fieldPoints = pointsByFieldId?.[fieldId];
    if (!fieldPoints || fieldPoints.length === 0) {
      return null;
    }

    return fieldPoints.reduce(
      (acc, point) => ({
        credits: acc.credits + point.credits,
        creditsInitial: acc.creditsInitial + (point.creditsInitial ?? 0),
        payout: acc.payout + point.payout,
        payoutInitial: acc.payoutInitial + (point.payoutInitial ?? 0),
      }),
      { credits: 0, creditsInitial: 0, payout: 0, payoutInitial: 0 }
    );
  };

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    refetch: query.refetch,
    fieldPracticePoints: filteredPoints,
    pointsByFieldId,
    payoutRates: query.data?.payoutRates,
    getFieldTotals,
  };
};
